import clsx from 'clsx'
import { useState } from 'react'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePageTitle } from '../../hooks/usePageTitle'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Modal from '../app/design/Modal'
import { RatePicker } from '../common/RatePicker'
import { Chip } from '../common/fields/Chip'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextArea } from '../common/fields/TextArea'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { learnMoreComponents } from './LearnMore'
import { deadlineOptions, feePercentOptions, requireCreditCardOptions } from './Options'

type Props =
    | {
          forOnboarding?: false
      }
    | {
          forOnboarding: true
          onSave: (user: User) => void
          onClose: () => void
      }

export function SetupCancellationPage({ forOnboarding, ...rest }: Props) {
    const me = PSApplication.me()
    const owner = PSApplication.owner()
    const [user, setUser] = useState(me.clone())
    const { logAndDisplayError } = useErrorHandler()
    const [currentModal, setCurrentModal] = useState<keyof typeof learnMoreComponents | null>(null)
    usePageTitle(forOnboarding ? 'Payment' : 'Cancellations')
    const { goBack } = useHistoryTrackerContext()

    const hasEdits =
        me.cancellationRate !== user.cancellationRate ||
        me.cancellationRateType !== user.cancellationRateType ||
        me.subscriptionCancellationRate !== user.subscriptionCancellationRate ||
        me.subscriptionCancellationRateType !== user.subscriptionCancellationRateType ||
        me.cancellationPolicy !== user.cancellationPolicy ||
        me.packageCancellationType !== user.packageCancellationType ||
        me.cancellationDuration !== user.cancellationDuration

    const onSave = async () => {
        const result = await RecordFactory.getInstance().sendUser(user)
        window.location.reload()
        if (!result.ok) {
            logAndDisplayError(result.error)
        }
    }

    const handleClose = 'onClose' in rest ? rest.onClose : undefined
    const handleSave = 'onSave' in rest ? rest.onSave : undefined

    return (
        <WorkflowPage
            title={forOnboarding ? undefined : 'Cancellations'}
            backButtonTitle={forOnboarding ? 'Back' : undefined}
            onBack={forOnboarding ? () => goBack() : undefined}
            onClose={handleClose}
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button
                        intent="primary"
                        disabled={!forOnboarding && !hasEdits}
                        onClick={() => {
                            if (handleSave) {
                                handleSave(user)
                            }
                            else {
                                onSave()
                            }
                        }}
                    >
                        {forOnboarding ? 'Next' : 'Save'}
                    </Button>
                </div>
            }
        >
            {forOnboarding && (
                <Heading size="xl" className="mt-[5px]">
                    Payment
                </Heading>
            )}
            <div className={clsx('mt-12 flex w-full flex-col items-center justify-start')}>
                <WorkflowPage.Card>
                    <WorkflowPage.IconSection
                        title="Late fee"
                        description="When an appointment is confirmed with a credit card and your client
                            cancels after the deadline, this fee will be charged automatically"
                        icon="money2.large"
                    >
                        <RatePicker
                            rateOptions={feePercentOptions}
                            amount={user.cancellationRate ?? 0}
                            rateType={user.cancellationRateType === 'rate' ? 'rate' : 'percent'}
                            onChange={(newRateType, newAmount) =>
                                setUser(
                                    user.clone({
                                        cancellation_rate_type: newRateType,
                                        cancellation_rate: newAmount,
                                    })
                                )
                            }
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Deadline"
                        icon="clock.large"
                        description="If your client confirms then cancels after this deadline they will be charged"
                    >
                        <SearchableSelect
                            size="medium"
                            className="border-gray-500"
                            items={deadlineOptions.map((option) => ({
                                id: option.value,
                                title: option.label,
                            }))}
                            onSelectItem={(item) =>
                                setUser(user.clone({ cancellation_duration: Number(item?.id) }))
                            }
                            selectedItemID={user.cancellationDuration?.toString()}
                            shouldPreserveLastSelection
                        />
                    </WorkflowPage.IconSection>

                    {!!owner.allowSubscription && (
                        <>
                            <WorkflowPage.IconSection title="Packages" icon="package2.large">
                                <div className="flex items-center gap-2">
                                    <Chip
                                        title="Charge fee"
                                        onClick={() =>
                                            setUser(
                                                user.clone({ package_cancellation_type: 'charge' })
                                            )
                                        }
                                        isSelected={user.packageCancellationType === 'charge'}
                                        size="small"
                                    />
                                    <Chip
                                        title="Lose session"
                                        onClick={() =>
                                            setUser(
                                                user.clone({ package_cancellation_type: 'session' })
                                            )
                                        }
                                        isSelected={user.packageCancellationType === 'session'}
                                        size="small"
                                    />
                                    <Chip
                                        title="Both"
                                        onClick={() =>
                                            setUser(
                                                user.clone({ package_cancellation_type: 'both' })
                                            )
                                        }
                                        isSelected={user.packageCancellationType === 'both'}
                                        size="small"
                                    />
                                </div>
                            </WorkflowPage.IconSection>

                            {forOnboarding && (
                                <WorkflowPage.IconSection
                                    title="Require credit card"
                                    icon="bankCard.large"
                                >
                                    <div className="flex items-center gap-2">
                                        {requireCreditCardOptions.map((option) => (
                                            <Chip
                                                key={option.value}
                                                title={option.label}
                                                onClick={() =>
                                                    setUser(
                                                        user.clone({
                                                            confirm_payment: option.value,
                                                        })
                                                    )
                                                }
                                                isSelected={user.confirmPayment === option.value}
                                                size="small"
                                            />
                                        ))}
                                    </div>
                                </WorkflowPage.IconSection>
                            )}

                            <WorkflowPage.IconSection
                                title="Subscription fee"
                                icon="refresh.large"
                                description="When a subscription is confirmed with a credit card and cancelled after the first payment this fee will automatically be charged"
                            >
                                <RatePicker
                                    amount={user.subscriptionCancellationRate ?? 0}
                                    rateOptions={feePercentOptions}
                                    rateType={
                                        user.subscriptionCancellationRateType === 'rate'
                                            ? 'rate'
                                            : 'percent'
                                    }
                                    onChange={(newRateType, newAmount) =>
                                        setUser(
                                            user.clone({
                                                subscription_cancellation_rate_type: newRateType,
                                                subscription_cancellation_rate: newAmount,
                                            })
                                        )
                                    }
                                />
                            </WorkflowPage.IconSection>
                        </>
                    )}

                    <WorkflowPage.IconSection
                        title="Cancellation Policy"
                        icon="calendarMinus.large"
                        thirdLine={
                            <button
                                className="text-primary-700 hover:text-primary-700/60 text-sm underline"
                                onClick={() => setCurrentModal('cancel-policy')}
                            >
                                Learn more
                            </button>
                        }
                    >
                        <TextArea
                            size="medium"
                            className="min-h-32"
                            placeholder="Add cancellation policy"
                            value={user.cancellationPolicy}
                            isAutoResizing
                            onChange={(event) =>
                                setUser(user.clone({ cancellation_policy: event.target.value }))
                            }
                            onClear={() => setUser(user.clone({ cancellation_policy: '' }))}
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>

            <Modal
                isOpen={currentModal !== null}
                onClose={() => setCurrentModal(null)}
                title={currentModal ? learnMoreComponents[currentModal].title : ''}
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button intent="primary" onClick={() => setCurrentModal(null)}>
                            Done
                        </Button>
                    </div>
                }
            >
                {currentModal && learnMoreComponents[currentModal].body}
            </Modal>

            <NavigationBlockerPrompt when={hasEdits} allowCurrentPaths={[/\/onboarding\/.*/]} />
        </WorkflowPage>
    )
}
